/**
 * TestimonialsSection — Public block showing published client reviews.
 *
 * If no published testimonial: shows a discreet "Ils m'ont fait confiance —
 * les premiers retours arrivent bientôt" placeholder (does NOT show the block
 * at all if the list is empty AND the caller opted out via `hideIfEmpty`).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Quote, MessageSquareQuote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ rating = 5 }) => (
  <div className="flex gap-0.5" aria-label={`${rating} sur 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={14}
        className={n <= rating ? 'text-[#d9fb06] fill-[#d9fb06]' : 'text-[#3f4816]'} />
    ))}
  </div>
);

export default function TestimonialsSection({ hideIfEmpty = false }) {
  const [items, setItems] = useState(null); // null=loading, []=empty, [...]=data
  useEffect(() => {
    axios.get(`${API_URL}/api/testimonials?limit=9`)
      .then((r) => setItems(r.data?.testimonials || []))
      .catch(() => setItems([]));
  }, []);

  if (items === null) return null;
  if (items.length === 0 && hideIfEmpty) return null;

  return (
    <section className="py-24 md:py-32 bg-[#1a1c1b]" data-testid="testimonials-section">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#d9fb06] text-xs font-bold uppercase tracking-widest mb-3">
            <MessageSquareQuote size={14} />
            Ils m&apos;ont fait confiance
          </div>
          <h2 className="font-black text-4xl md:text-5xl leading-tight text-[#d9fb06] uppercase">
            {items.length === 0 ? 'Les premiers retours arrivent bientôt' : 'Ce qu\'ils en disent'}
          </h2>
        </div>

        {items.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center border-2 border-dashed border-[#3f4816] rounded-2xl p-10">
            <Quote className="mx-auto text-[#3f4816] mb-4" size={40} />
            <p className="text-[#dfddd6] mb-2">
              Cette section accueillera les vrais retours de mes clients.
            </p>
            <p className="text-[#888680] text-sm">
              Chaque avis publié ici est nominatif et vérifié — aucun avis inventé, jamais.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((t) => (
              <article
                key={t.id}
                data-testid={`testimonial-card-${t.id}`}
                className="relative bg-[#302f2c] border border-[#3f4816] rounded-2xl p-6 flex flex-col hover:border-[#d9fb06]/60 transition-colors"
              >
                <Quote className="text-[#d9fb06] mb-3" size={22} />
                <p className="text-[#dfddd6] text-sm leading-relaxed mb-4 flex-1">
                  {t.content}
                </p>
                <div className="flex items-center justify-between mt-auto pt-3 border-t border-[#3f4816]">
                  <div className="min-w-0">
                    <p className="text-[#d9fb06] font-bold text-sm truncate">{t.author_name}</p>
                    {t.author_role && <p className="text-[#888680] text-xs truncate">{t.author_role}</p>}
                  </div>
                  <Stars rating={t.rating || 5} />
                </div>
                {t.project_type && (
                  <span className="absolute top-4 right-4 text-[10px] uppercase bg-[#d9fb06]/10 text-[#d9fb06] px-2 py-0.5 rounded">
                    {t.project_type}
                  </span>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
